import clsx from 'clsx';
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClientRole } from '@/api';
import { useMyPrefs, useSaveMyPrefs } from '@/api/profiles';
import { findAnchor, isPhone, onOpenTour, tourSteps } from '@/lib/tour';

type Box = { top: number; left: number; width: number; height: number };

const PAD = 6;
const GAP = 12;
const FOCUSABLE = 'button:not([disabled])';

/** The viewport rectangle of an element, grown a little so the ring does not touch the text. */
function boxOf(el: HTMLElement): Box {
  const r = el.getBoundingClientRect();
  return { top: r.top - PAD, left: r.left - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2 };
}

/**
 * The client's first-login guide (MK42 «Как пользоваться кабинетом»): a few coach-marks over the real
 * navigation, a centered card on a phone. Shown once per person (`tourSeenAt` in their prefs, so
 * another device does not ask again) and re-openable from the profile and the menus.
 */
export function Tour() {
  const role = useClientRole();
  const prefs = useMyPrefs();
  const save = useSaveMyPrefs();
  const navigate = useNavigate();
  const [active, setActive] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [pos, setPos] = useState(0);
  const [version, setVersion] = useState(0);
  const [box, setBox] = useState<Box | null>(null);
  const [height, setHeight] = useState(200);
  const panel = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const textId = useId();

  const all = useMemo(() => tourSteps(role), [role]);
  const phone = active && isPhone();

  // First visit: wait for the page to draw so the places the guide points at exist.
  const unseen = !!role && prefs.isSuccess && !prefs.data.tourSeenAt && !dismissed;
  useEffect(() => {
    if (!unseen || active) return;
    const t = setTimeout(() => (setPos(0), setActive(true)), 600);
    return () => clearTimeout(t);
  }, [unseen, active]);

  useEffect(
    () =>
      onOpenTour(() => {
        // The project is where most places are; the summary redirects there when there is one project.
        navigate('/');
        setTimeout(() => (setPos(0), setActive(true)), 400);
      }),
    [navigate],
  );

  // The page changes under the guide (data arrives, a route changes): look again.
  useEffect(() => {
    if (!active) return;
    let frame = 0;
    const bump = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setVersion((v) => v + 1));
    };
    const mo = new MutationObserver((recs) => {
      // The guide's own redraws are not the page changing.
      if (recs.some((r) => !(r.target instanceof Element ? r.target : r.target.parentElement)?.closest('[data-testid="tour"]'))) bump();
    });
    mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'class', 'style'] });
    window.addEventListener('resize', bump);
    window.addEventListener('scroll', bump, true);
    return () => {
      mo.disconnect();
      window.removeEventListener('resize', bump);
      window.removeEventListener('scroll', bump, true);
      cancelAnimationFrame(frame);
    };
  }, [active]);

  // A step whose place is not on the screen is skipped; a phone has no places, only text.
  const present = useMemo(
    () => (active ? all.map((s, order) => ({ ...s, order })).filter((s) => phone || findAnchor(s.anchor)) : []),
    // `version` stands for «the DOM changed».
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [active, all, phone, version],
  );
  const current = present.find((s) => s.order >= pos) ?? present.at(-1);
  const index = current ? present.indexOf(current) : -1;
  const anchor = current && !phone ? findAnchor(current.anchor) : null;

  useLayoutEffect(() => {
    if (!anchor) return setBox(null);
    setBox(boxOf(anchor));
    if (panel.current) setHeight(panel.current.offsetHeight);
  }, [anchor, version, current?.id]);

  // Bring the place into view when the step changes.
  useEffect(() => {
    anchor?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    // Only on a new step, not on every look at the DOM.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id]);

  const close = useCallback(() => {
    setActive(false);
    setDismissed(true);
    // Skip and finish are the same fact: the person has seen it. The server keeps the first moment.
    if (prefs.data) save.mutate({ ...prefs.data, tourSeenAt: new Date().toISOString() });
  }, [prefs.data, save]);

  const next = useCallback(() => {
    if (!current) return;
    const after = present.find((s) => s.order > current.order);
    if (after) setPos(after.order);
    else close();
  }, [current, present, close]);
  const back = useCallback(() => {
    if (!current) return;
    const before = present.filter((s) => s.order < current.order).at(-1);
    if (before) setPos(before.order);
  }, [current, present]);

  // Keys, and focus kept inside the card; focus goes back to what had it.
  const shown = active && !!current;
  useEffect(() => {
    if (!shown) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    panel.current?.querySelector<HTMLElement>('[data-autofocus]')?.focus();
    return () => {
      if (opener && document.contains(opener)) opener.focus();
    };
  }, [shown]);
  useEffect(() => {
    if (!shown) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const onButton = document.activeElement instanceof HTMLButtonElement && !!panel.current?.contains(document.activeElement);
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        close();
      } else if (e.key === 'ArrowRight' || (e.key === 'Enter' && !onButton)) {
        e.preventDefault();
        next();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        back();
      } else if (e.key === 'Tab') {
        const items = [...(panel.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])];
        if (!items.length) return;
        const at = items.indexOf(document.activeElement as HTMLElement);
        const to = e.shiftKey ? (at <= 0 ? items.length - 1 : at - 1) : at === items.length - 1 ? 0 : at + 1;
        e.preventDefault();
        items[to].focus();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [shown, close, next, back]);

  if (!shown || !current) return null;
  const last = index === present.length - 1;
  const place = (() => {
    if (!box) return {};
    const width = Math.min(340, window.innerWidth - 24);
    const left = Math.max(12, Math.min(box.left, window.innerWidth - width - 12));
    const room = window.innerHeight - (box.top + box.height);
    if (room >= height + GAP + 12) return { top: box.top + box.height + GAP, left, width };
    if (box.top >= height + GAP + 12) return { top: box.top - height - GAP, left, width };
    return { bottom: 16, left: Math.max(12, (window.innerWidth - width) / 2), width };
  })();

  return (
    <div className="fixed inset-0 z-[60]" data-testid="tour">
      {/* Takes clicks so the page underneath is not driven mid-guide. */}
      <div className={clsx('absolute inset-0', (phone || !box) && 'bg-ink/30')} aria-hidden />
      {!phone && box && <div aria-hidden className="pointer-events-none fixed rounded-md ring-1 ring-ink/60" style={{ ...box, boxShadow: '0 0 0 9999px rgb(var(--ink) / 0.3)' }} />}
      <div className={clsx(phone || !box ? 'absolute inset-0 grid place-items-center p-4' : 'contents')}>
        <div
          ref={panel}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={textId}
          className={clsx('rounded-lg bg-canvas p-4 shadow-pop', phone || !box ? 'w-full max-w-sm' : 'fixed')}
          style={phone || !box ? undefined : place}
        >
          <p className="num text-xs text-muted">
            Шаг {index + 1} из {present.length}
          </p>
          <h2 id={titleId} className="mt-1 text-base font-semibold">
            {current.title}
          </h2>
          <p id={textId} className="mt-1.5 text-sm text-muted">
            {current.text}
            {phone && ` ${current.phoneWhere}`}
          </p>
          <div className="mt-4 flex items-center gap-2">
            <button type="button" className="btn-ghost -ml-3" onClick={close}>
              Пропустить
            </button>
            <span className="ml-auto" />
            {index > 0 && (
              <button type="button" className="btn-secondary" onClick={back}>
                Назад
              </button>
            )}
            <button type="button" className="btn-primary" data-autofocus onClick={next}>
              {last ? 'Готово' : 'Далее'}
            </button>
          </div>
          <p className="mt-3 text-xs text-faint">Вернуться к подсказкам можно в «Мой профиль».</p>
        </div>
      </div>
    </div>
  );
}
